import React, {useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {filterFactories, filterFactoryLines} from 'api/orders';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {AsyncSelect, Select, SelectOption} from 'components/ui-kit/Select';
import {ALPHA_2_COUNTRY} from 'core/constants/env';
import {localOrImporterType$, profileState$} from 'models/auth';
import {firstStep$, firstStepChanged, stepChanged} from 'models/orders/create';
import {profileCountriesState$} from 'models/profile/common';
import {OrderFormData} from 'types';

import {ProfileCountries} from 'types/profile';

import {
    FormContainer,
    NavigationButtonsContainer,
    OrderFormWrapper,
    OrderFormWrapperTitle,
    OrderFormWrapperTitleValue,
} from '../styled';

import {ProductTypes} from './productTypes';

const schema = yup.object().shape({
    country: yup
        .object({
            value: yup.string().required(i18next.t('components.form.required')),
            label: yup.mixed().required(i18next.t('components.form.required')),
        })
        .required(i18next.t('components.form.required')),
    productTypes: yup.string().required(),
    factoryId: yup
        .object({
            value: yup.string(),
            label: yup.mixed(),
        })
        .when('productTypes', {
            is: 'local',
            then: (fieldSchema) => fieldSchema.required(i18next.t('components.form.required')),
            otherwise: (fieldSchema) => fieldSchema.notRequired().nullable(),
        }),
    productionLineId: yup
        .object({
            value: yup.string(),
            label: yup.mixed(),
        })
        .when('productTypes', {
            is: 'local',
            then: (fieldSchema) => fieldSchema.required(i18next.t('components.form.required')),
            otherwise: (fieldSchema) => fieldSchema.notRequired().nullable(),
        }),
    manufName: yup.string().when('productTypes', {
        is: 'import',
        then: (fieldSchema) => fieldSchema.required(i18next.t('components.form.required')),
        otherwise: (fieldSchema) => fieldSchema.notRequired().nullable(),
    }),
    manufAddress: yup.string().when('productTypes', {
        is: 'import',
        then: (fieldSchema) => fieldSchema.required(i18next.t('components.form.required')),
        otherwise: (fieldSchema) => fieldSchema.notRequired().nullable(),
    }),
});

export const OrderForm = () => {
    const {t} = useTranslation();
    const firstStep = useStore(firstStep$);
    const {data: factoryCountries} = useStore(profileCountriesState$);
    const localOrImporterType = useStore(localOrImporterType$);
    const {profile} = useStore(profileState$);

    useEffect(() => {
        if (firstStep.productTypes) {
            return;
        }

        if (!localOrImporterType) {
            return;
        }

        if (localOrImporterType === 'both') {
            firstStepChanged({productTypes: 'both'});
            return;
        }

        firstStepChanged({productTypes: localOrImporterType as 'local' | 'import'});
    }, [localOrImporterType]);

    const countries = useMemo(() => {
        if (firstStep.productTypes === 'local') {
            return [];
        }
        return (
            factoryCountries.map(({code, countryFr, country}: ProfileCountries) => ({
                label: i18next.language === 'en' ? country : countryFr,
                value: code,
            })) || []
        );
    }, [t, factoryCountries, firstStep.productTypes]);

    const {control, handleSubmit, formState, setValue, watch, trigger, reset} = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            ...firstStep,
            productTypes: firstStep.productTypes ?? '',
        },
    });

    const factoryId = watch('factoryId', null)?.value;

    const loadOptions = async (filter: string) => {
        let data = [];
        if (factoryId) {
            data = await filterFactoryLines(factoryId);
            data = data
                .map((value: any) => ({
                    value: value.id,
                    label: value.name,
                    meta: {
                        name: value.name ?? '',
                    },
                }))
                .filter(({label}: {label: string}) =>
                    label.toLowerCase().includes(filter.toLowerCase()),
                );
        }
        return data;
    };

    const onHandleChanged = (data: OrderFormData) => {
        firstStepChanged({
            ...data,
            ...(firstStep.productTypes === 'local' && {
                manufName: profile?.legalPerson?.company_name,
                manufAddress: profile?.legalPerson?.legal_address,
            }),
        });
        stepChanged(1);
    };

    useEffect(() => {
        setValue('productTypes', firstStep.productTypes, {shouldValidate: true});
        if (firstStep.productTypes === 'local') {
            setValue('country', {
                label: t(`dictionaries.countries.${ALPHA_2_COUNTRY}`),
                value: ALPHA_2_COUNTRY,
            });
        }
    }, [firstStep.productTypes]);

    const mapOptions = (options: any[], filter: string) =>
        options
            .filter(({countryCode}: {countryCode: string}) => countryCode === ALPHA_2_COUNTRY)
            .map((value) => ({
                value: value.id,
                label: value.name,
                meta: {
                    name: value.name ?? '',
                },
            }))
            .filter(({label}: {label: string}) =>
                label.toLowerCase().includes(filter.toLowerCase()),
            );

    const clearProductTypes = () => {
        firstStepChanged({
            productTypes: 'both',
            country: null,
            factoryId: null,
            productionLineId: null,
            manufName: '',
            manufAddress: '',
        });

        reset({
            country: null,
            factoryId: null,
            productionLineId: null,
            manufName: '',
            manufAddress: '',
            productTypes: 'both',
        });
    };

    return firstStep.productTypes === 'both' ? (
        <ProductTypes />
    ) : (
        <FormContainer>
            {localOrImporterType === 'both' && (
                <OrderFormWrapper>
                    <OrderFormWrapperTitle>
                        {t('pages.orders.form.orderType')}
                    </OrderFormWrapperTitle>
                    <OrderFormWrapperTitleValue>
                        {firstStep.productTypes === 'local'
                            ? 'Locally produced products'
                            : 'Imported products'}
                    </OrderFormWrapperTitleValue>
                    <div>
                        <Button
                            variant={ButtonVariant.Text}
                            type="button"
                            onClick={() => {
                                clearProductTypes();
                            }}
                        >
                            {t('pages.orders.form.change')}
                        </Button>
                    </div>
                </OrderFormWrapper>
            )}
            <form onSubmit={handleSubmit(onHandleChanged)}>
                <Grid container spacing={3}>
                    <Grid item md={4} xs={12}>
                        <Controller
                            control={control}
                            name="country"
                            render={({value, onChange}, {invalid}) => (
                                <Select
                                    isRequired={true}
                                    disabled={firstStep.productTypes === 'local'}
                                    onChange={(val: SelectOption<string>) => {
                                        onChange(val);
                                        setValue && setValue(`productionLineId`, null);
                                        setValue && setValue(`factoryId`, null);
                                        trigger && trigger('productionLineId');
                                    }}
                                    value={value}
                                    label={t('pages.orders.form.country')}
                                    hasError={invalid}
                                    options={countries}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item md={4} xs={12} style={{display: 'none'}}>
                        <Controller
                            control={control}
                            name={`productTypes`}
                            render={(props, {invalid}) => (
                                <Input {...props} hasError={invalid} value={props.value} />
                            )}
                        />
                    </Grid>
                    {firstStep.productTypes === 'local' ? (
                        <>
                            <Grid item md={4} xs={12}>
                                <Controller
                                    control={control}
                                    name="factoryId"
                                    render={(props) => (
                                        <AsyncSelect
                                            {...props}
                                            label={t('pages.orders.form.factoryName')}
                                            isRequired={true}
                                            defaultOptions
                                            loadOptions={async (filter: string) => {
                                                const data = await filterFactories();
                                                return mapOptions(data, filter);
                                            }}
                                            placeholder={t(
                                                'pages.orders.form.selectProductionLine',
                                            )}
                                            onChange={(val: {
                                                value: string;
                                                meta: {name: string};
                                            }) => {
                                                props.onChange(val);
                                                setValue && setValue(`productionLineId`, null);
                                                trigger && trigger('productionLineId');
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <Controller
                                    control={control}
                                    name="productionLineId"
                                    render={(props) => (
                                        <AsyncSelect
                                            {...props}
                                            key={factoryId}
                                            disabled={!factoryId}
                                            label={t('pages.orders.form.productionLineName')}
                                            isRequired={true}
                                            defaultOptions
                                            loadOptions={loadOptions}
                                            placeholder={t(
                                                'pages.orders.form.selectProductionLine',
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item md={4} xs={12}>
                                <Controller
                                    control={control}
                                    name={`manufName`}
                                    render={(props, {invalid}) => (
                                        <Input
                                            {...props}
                                            isRequired={true}
                                            label={t(
                                                'pages.orders.form.foreignManufacturerNameLabel',
                                            )}
                                            placeholder={t(
                                                'pages.orders.form.foreignManufacturerNamePlaceholder',
                                            )}
                                            hasError={invalid}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <Controller
                                    control={control}
                                    name={`manufAddress`}
                                    render={(props, {invalid}) => (
                                        <Input
                                            {...props}
                                            isRequired={true}
                                            label={t(
                                                'pages.orders.form.foreignManufacturerAddressLabel',
                                            )}
                                            placeholder={t(
                                                'pages.orders.form.foreignManufacturerAddressPlaceholder',
                                            )}
                                            hasError={invalid}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
                <NavigationButtonsContainer>
                    <span />
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={formState.isValid === false}
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </NavigationButtonsContainer>
            </form>
        </FormContainer>
    );
};
